import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusFetch } from '../../nexus/nexusClient';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { postmasterIncidentsFixture } from './fixture';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-postmaster-soc.md
 * Endpoints: /v1/admin/postmaster/incidents
 *
 * REST-only — WebSocket-Stream folgt in Iteration 2. Aktuell pollen wir
 * alle 15s wie in INBOX vorgeschlagen.
 */

/** GET /v1/admin/postmaster/incidents?status=&provider=&ip= */
export function usePostmasterIncidents({ status = '', provider = '', ip = '' } = {}) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-postmaster', { status, provider, ip }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return postmasterIncidentsFixture({ status, provider, ip });
      }
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (provider) qs.set('provider', provider);
      if (ip) qs.set('ip', ip);
      const res = await nexusFetch(`/v1/admin/postmaster/incidents?${qs.toString()}`);
      const list = res?.incidents ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { incidents: list, raw: res };
    },
    refetchInterval: 15_000,
  });
}

/** PATCH /v1/admin/postmaster/incidents/:id — manueller State-Wechsel */
export function useUpdatePostmasterIncident() {
  return useApiMutation({
    live: (_fetch, { id, status }) =>
      nexusFetch(`/v1/admin/postmaster/incidents/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: { status },
      }),
    fixture: ({ id, status }) => ({ ok: true, id, status }),
    invalidate: ['nexus-postmaster'],
    successMessage: (_, { id, status }) => `Incident ${id} → ${status}.`,
  });
}

/** POST /v1/admin/postmaster/incidents/:id/retry-delisting */
export function useRetryDelisting() {
  return useApiMutation({
    live: (_fetch, { id }) =>
      nexusFetch(`/v1/admin/postmaster/incidents/${encodeURIComponent(id)}/retry-delisting`, {
        method: 'POST',
      }),
    fixture: ({ id }) => ({ ok: true, id, status: 'delisting_requested' }),
    invalidate: ['nexus-postmaster'],
    successMessage: (_, { id }) =>
      `Delisting-Request für ${id} enqueued (Sub-Plugins folgen — siehe INBOX-Hinweis).`,
  });
}
